/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 25th 2022, 8:33:07 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { WritableModel } from "lib/schemas/abstract";
import { schemaBuilder, Many2OneIntPKBaseSchema, fileBaseSchema } from "lib/validation";
import { isPhoneNumber } from "lib/validation";

export enum Gender {
  male = "Male",
  female = "Female",
}
export interface RegistrationProfile extends WritableModel<false> {
  image?: any;
  dob: Date;
  surname: string;
  gender: keyof typeof Gender;
  firstname: string;
  middlename: string;
  national_id: string;
  province: string;
  district: string;
  phone_number: string;
  readonly activated: boolean;
  biography: string;
  user: number;
}

export type RegistrationProfilePatch = Required<Pick<RegistrationProfile, "id">> & Partial<RegistrationProfile>;

type WriteableRegistrationProfile = Omit<RegistrationProfile, "activated">;

export const RegistrationProfileValidationSchema: schemaBuilder.SchemaOf<WriteableRegistrationProfile> = schemaBuilder
  .object()
  .shape({
    id: schemaBuilder.number().nullable(),
    image: fileBaseSchema({ sizeLimit: 2097152 }).optional().nullable(),
    dob: schemaBuilder.date().required("Date of birth is required.").typeError("Date of birth is required."),
    firstname: schemaBuilder.string().required("First name is required."),
    surname: schemaBuilder.string().required("Surname is required."),
    gender: schemaBuilder.mixed().oneOf(Object.keys(Gender)).required("Gender is required"),
    middlename: schemaBuilder.string().optional().default("").nullable(),
    national_id: schemaBuilder
      .string()
      .required("National ID or Passport is required")
      .typeError("National ID or Passport is required"),
    province: schemaBuilder.string().optional().default("").nullable(),
    district: schemaBuilder.string().optional().default("").nullable(),
    phone_number: schemaBuilder
      .string()
      .nullable()
      .test("valid-phone-number", "Please provide a valid phone number", (v) => isPhoneNumber(v)),
    biography: schemaBuilder.string().optional().default("").nullable(),
    user: Many2OneIntPKBaseSchema().optional().nullable(),
  });

export const ProfileImagePatchValidationSchema: schemaBuilder.SchemaOf<
  Required<Pick<RegistrationProfile, "id" | "image">>
> = schemaBuilder.object().shape({
  id: schemaBuilder.number().required("Image can only be updated for an existing profile"),
  image: fileBaseSchema({ sizeLimit: 2097152 }).required("Image is required"),
});
